/**
 * Bank fees section (intermediary + destination) and TDS section.
 */

import { Input, SearchableSelect } from '@elsapiens/ui'

import { CurrencyInput } from '../ui'

interface RecordPaymentFeesProps {
  invoiceCurrency: string
  baseCurrency: string
  intermediaryFee: string
  setIntermediaryFee: (v: string) => void
  hasSeparateIntermediaryFee: boolean
  setSeparateIntermediaryFee: (v: boolean) => void
  destinationFee: string
  setDestinationFee: (v: string) => void
  hasSeparateDestinationFee: boolean
  setSeparateDestinationFee: (v: boolean) => void
  feeAccountId: string
  setFeeAccountId: (v: string) => void
  feeAccountOptions: { value: string; label: string }[]
  tdsAmount: string
  setTdsAmount: (v: string) => void
  tdsAccountId: string
  setTdsAccountId: (v: string) => void
  tdsAccountOptions: { value: string; label: string }[]
}

interface FeeFieldProps {
  label: string
  value: string
  onChange: (v: string) => void
  hasSeparate: boolean
  setSeparate: (v: boolean) => void
}

function FeeField({ label, value, onChange, hasSeparate, setSeparate }: FeeFieldProps): JSX.Element {
  return (
    <div>
      <div className="text-xs font-semibold text-foreground-secondary mb-1">{label}</div>
      <Input
        hideMessage placeholder="0.00" size="sm" step="any" type="number"
        value={value} onChange={e => onChange(e.target.value)}
      />
      {parseFloat(value) > 0 && (
        <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
          <input
            checked={hasSeparate}
            className="w-3.5 h-3.5 rounded border-border text-primary focus:ring-ring"
            name="separate-fee-entry"
            type="checkbox"
            onChange={e => setSeparate(e.target.checked)}
          />
          <span className="text-[11px] text-foreground-secondary">Separate entry (easier to reconcile)</span>
        </label>
      )}
    </div>
  )
}

interface BankFeesProps {
  invoiceCurrency: string
  baseCurrency: string
  intermediaryFee: string
  setIntermediaryFee: (v: string) => void
  hasSeparateIntermediaryFee: boolean
  setSeparateIntermediaryFee: (v: boolean) => void
  destinationFee: string
  setDestinationFee: (v: string) => void
  hasSeparateDestinationFee: boolean
  setSeparateDestinationFee: (v: boolean) => void
  feeAccountId: string
  setFeeAccountId: (v: string) => void
  feeAccountOptions: { value: string; label: string }[]
  wideStyle: React.CSSProperties
}

function BankFeesSection({
  invoiceCurrency, baseCurrency,
  intermediaryFee, setIntermediaryFee, hasSeparateIntermediaryFee, setSeparateIntermediaryFee,
  destinationFee, setDestinationFee, hasSeparateDestinationFee, setSeparateDestinationFee,
  feeAccountId, setFeeAccountId, feeAccountOptions, wideStyle,
}: BankFeesProps): JSX.Element {
  const hasFees = parseFloat(intermediaryFee) > 0 || parseFloat(destinationFee) > 0
  return (
    <div className="rounded-lg border border-border el-p-md bg-background" style={wideStyle}>
      <div className="text-xs font-semibold text-foreground-secondary uppercase tracking-wide el-mb-sm">Bank Fees</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 el-gap-field">
        <FeeField hasSeparate={hasSeparateIntermediaryFee} label={`Intermediary Fee (${invoiceCurrency})`} setSeparate={setSeparateIntermediaryFee} value={intermediaryFee} onChange={setIntermediaryFee} />
        <FeeField hasSeparate={hasSeparateDestinationFee} label={`Destination Fee (${baseCurrency})`} setSeparate={setSeparateDestinationFee} value={destinationFee} onChange={setDestinationFee} />
      </div>
      {hasFees && (
        <div className="el-mt-sm">
          <div className="text-xs font-semibold text-foreground-secondary mb-1">Fee Expense Account</div>
          <SearchableSelect hideMessage options={feeAccountOptions} placeholder="Select fee expense account..." value={feeAccountId || undefined} onChange={(val) => setFeeAccountId(val || '')} />
        </div>
      )}
    </div>
  )
}

interface TdsSectionProps {
  invoiceCurrency: string
  tdsAmount: string
  setTdsAmount: (v: string) => void
  tdsAccountId: string
  setTdsAccountId: (v: string) => void
  tdsAccountOptions: { value: string; label: string }[]
  wideStyle: React.CSSProperties
}

function TdsSection({ invoiceCurrency, tdsAmount, setTdsAmount, tdsAccountId, setTdsAccountId, tdsAccountOptions, wideStyle }: TdsSectionProps): JSX.Element {
  return (
    <div className="rounded-lg border border-border el-p-md bg-background" style={wideStyle}>
      <div className="text-xs font-semibold text-foreground-secondary uppercase tracking-wide el-mb-sm">TDS (Tax Deducted at Source)</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 el-gap-field">
        <div>
          <div className="text-xs font-semibold text-foreground-secondary mb-1">TDS Amount ({invoiceCurrency})</div>
          <CurrencyInput hideMessage currency={invoiceCurrency} size="sm" value={tdsAmount ? parseFloat(tdsAmount) : null} onChange={(val) => setTdsAmount(val ? String(val) : '')} />
        </div>
        <div>
          <div className="text-xs font-semibold text-foreground-secondary mb-1">TDS Account</div>
          {tdsAccountOptions.length > 0 ? (
            <SearchableSelect hideMessage options={tdsAccountOptions} placeholder="Select TDS account..." value={tdsAccountId || undefined} onChange={(val) => setTdsAccountId(val || '')} />
          ) : (
            <p className="text-xs text-foreground-secondary italic el-py-xs">No TDS accounts found (1420 / 2130).</p>
          )}
        </div>
      </div>
    </div>
  )
}

export function RecordPaymentFees({
  invoiceCurrency, baseCurrency,
  intermediaryFee, setIntermediaryFee, hasSeparateIntermediaryFee, setSeparateIntermediaryFee,
  destinationFee, setDestinationFee, hasSeparateDestinationFee, setSeparateDestinationFee,
  feeAccountId, setFeeAccountId, feeAccountOptions,
  tdsAmount, setTdsAmount, tdsAccountId, setTdsAccountId, tdsAccountOptions,
}: RecordPaymentFeesProps): JSX.Element {
  const wideStyle: React.CSSProperties = {
    marginInline: 'calc(var(--el-container-px) * -0.5)',
    width: 'calc(100% + var(--el-container-px))',
  }
  return (
    <>
      <BankFeesSection
        baseCurrency={baseCurrency} destinationFee={destinationFee} feeAccountId={feeAccountId}
        feeAccountOptions={feeAccountOptions} hasSeparateDestinationFee={hasSeparateDestinationFee}
        hasSeparateIntermediaryFee={hasSeparateIntermediaryFee} intermediaryFee={intermediaryFee}
        invoiceCurrency={invoiceCurrency} setDestinationFee={setDestinationFee}
        setFeeAccountId={setFeeAccountId} setIntermediaryFee={setIntermediaryFee}
        setSeparateDestinationFee={setSeparateDestinationFee} setSeparateIntermediaryFee={setSeparateIntermediaryFee}
        wideStyle={wideStyle}
      />
      <TdsSection
        invoiceCurrency={invoiceCurrency} setTdsAccountId={setTdsAccountId}
        setTdsAmount={setTdsAmount} tdsAccountId={tdsAccountId}
        tdsAccountOptions={tdsAccountOptions} tdsAmount={tdsAmount} wideStyle={wideStyle}
      />
    </>
  )
}
